/* eslint-disable */
/**
 * Maps — wrapper around the Google Maps JavaScript API.
 * Provides all map interactions needed by PondMapView: data layer management,
 * polygon drawing, reverse geocoding, and place autocomplete.
 */
import { colorTokens } from '@eruvaka/web-pondlogs-base';
export class Maps {
    /** Underlying google.maps.Map instance. */
    map;
    mapData;
    geocoder;
    currentPolygon = null;
    editingFeatureId = null;
    highlightedFeatureId = null;
    isDimmed = false;
    currentPolygonPathListeners = [];
    searchedMarker = null;
    pondLabelOverlays = [];
    // Custom draw-mode state (replaces deprecated DrawingManager removed in v3.65)
    drawListeners = [];
    drawVertices = [];
    drawPreview = null;
    drawRubberBand = null;
    eventListeners = [];
    dataListeners = [];
    constructor(divElement) {
        this.map = new google.maps.Map(divElement);
        this.mapData = new google.maps.Data();
        this.geocoder = new google.maps.Geocoder();
    }
    /**
     * Tears down all in-progress drawing state: listeners, preview polygon,
     * rubber-band line, vertex list, cursor override, and double-click zoom.
     */
    clearDrawingMode() {
        this.drawListeners.forEach((l) => l.remove());
        this.drawListeners = [];
        this.drawVertices = [];
        this.drawPreview?.setMap(null);
        this.drawPreview = null;
        this.drawRubberBand?.setMap(null);
        this.drawRubberBand = null;
        this.map.setOptions({ draggableCursor: '', disableDoubleClickZoom: false });
    }
    /**
     * Configures map appearance.
     * - 'MAP'  → satellite hybrid view with standard controls
     * - 'DATA' → applies DO-based fill colours to the data layer and attaches it to the map
     */
    setOptions(mode) {
        if (mode === 'MAP') {
            this.map.setOptions({
                mapTypeId: google.maps.MapTypeId.HYBRID,
                center: { lat: 15.9, lng: 80.2 },
                zoom: 16,
                minZoom: 4,
                zoomControl: true,
                disableDefaultUI: true,
                fullscreenControl: true,
                clickableIcons: false,
                fullscreenControlOptions: {
                    position: google.maps.ControlPosition.RIGHT_CENTER,
                },
            });
        }
        else {
            this.applyDataStyle(this.isDimmed);
            this.mapData.setMap(this.map);
        }
    }
    /**
     * Applies the DO-based fill style to the data layer.
     * When `dim` is true, reduces opacity and disables pointer events on all pond polygons
     * (used while the onboarding DrawPondPanel is active).
     */
    applyDataStyle(dim) {
        this.mapData.setStyle((feature) => {
            const status = feature.getProperty('status');
            const doValue = feature.getProperty('doValue');
            const criticalLower = feature.getProperty('criticalLowerLimit') ?? 3;
            const lower = feature.getProperty('lowerLimit') ?? 4;
            let fillColor = colorTokens.secondary;
            if (!status || status.toUpperCase() !== 'ACTIVE') {
                fillColor = colorTokens.inactive;
            }
            else if (doValue != null && doValue < criticalLower) {
                fillColor = colorTokens.error;
            }
            else if (doValue != null && doValue < lower) {
                fillColor = colorTokens.yellow;
            }
            return {
                fillColor,
                strokeColor: fillColor,
                fillOpacity: dim ? 0.15 : 0.45,
                strokeWeight: dim ? 1 : 2,
                strokeOpacity: dim ? 0.3 : 1,
                clickable: !dim,
            };
        });
    }
    /**
     * Dims (or un-dims) all pond polygons on the map.
     * Call with `true` when the onboarding panel is active to prevent interaction
     * with existing ponds. Call with `false` to restore normal appearance.
     */
    setDimMode(dim) {
        this.isDimmed = dim;
        this.applyDataStyle(dim);
    }
    /** Attaches a no-op click listener so the map surface is interactive. */
    setMapListener() {
        const l = this.map.addListener('click', () => {
            /* no-op — click handling is delegated to the data layer */
        });
        this.eventListeners.push(l);
    }
    /**
     * Attaches a Google Places Autocomplete to the given input element.
     * When the user selects a place, the map pans/fits to it and `callback` is invoked.
     */
    initAutoComplete(input, callback) {
        const autocomplete = new google.maps.places.Autocomplete(input);
        autocomplete.bindTo('bounds', this.map);
        autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            if (!place.geometry?.location)
                return;
            if (place.geometry.viewport) {
                this.map.fitBounds(place.geometry.viewport);
            }
            else {
                this.map.setCenter(place.geometry.location);
                this.map.setZoom(17);
            }
            if (this.searchedMarker) {
                this.searchedMarker.setPosition(place.geometry.location);
                this.searchedMarker.setVisible(true);
            }
            else {
                this.searchedMarker = new google.maps.Marker({
                    map: this.map,
                    position: place.geometry.location,
                    anchorPoint: new google.maps.Point(0, -29),
                });
            }
            callback();
        });
    }
    setMapCenter(center) {
        this.map.setCenter(center);
    }
    panTo(center) {
        this.map.panTo(center);
    }
    /**
     * Replaces all features in the data layer with the supplied set.
     * Existing features are removed before the new ones are added.
     */
    setMapData(features) {
        this.mapData.forEach((f) => this.mapData.remove(f));
        features.forEach((f) => this.mapData.add(f));
        this.highlightedFeatureId = null;
    }
    /**
     * Highlights the polygon for a specific pond with a bright white stroke.
     * Pass null to clear any existing highlight.
     */
    highlightPond(featureId) {
        if (this.highlightedFeatureId) {
            const prev = this.mapData.getFeatureById(this.highlightedFeatureId);
            if (prev)
                this.mapData.revertStyle(prev);
            this.highlightedFeatureId = null;
        }
        if (!featureId)
            return;
        const feature = this.mapData.getFeatureById(featureId);
        if (!feature)
            return;
        this.mapData.overrideStyle(feature, {
            strokeColor: '#FFFFFF',
            strokeWeight: 4,
            fillOpacity: 0.6,
        });
        this.highlightedFeatureId = featureId;
    }
    /**
     * Renders pond name labels at the supplied centroid positions using DOM overlays.
     * Any previously rendered labels are removed first.
     */
    setPondLabels(labels) {
        this.clearPondLabels();
        const map = this.map;
        labels.forEach(({ position, name, color }) => {
            const text = name.length > 16 ? name.slice(0, 15) + '\u2026' : name;
            // Use dark text on light backgrounds (inactive gray)
            const textColor = color === colorTokens.inactive ? colorTokens.gray700 : colorTokens.white;
            const div = document.createElement('div');
            div.textContent = text;
            div.style.cssText =
                `position:absolute;background:${color};color:${textColor};` +
                    'font-size:10px;font-weight:700;font-family:Nunito,Roboto,Arial,sans-serif;' +
                    'padding:2px 6px;border-radius:3px;white-space:nowrap;' +
                    'pointer-events:none;transform:translate(-50%,-50%);line-height:1.4;opacity:0.92;';
            const overlay = new google.maps.OverlayView();
            overlay.onAdd = () => {
                const panes = overlay.getPanes();
                if (panes)
                    panes.markerLayer.appendChild(div);
            };
            overlay.draw = () => {
                const proj = overlay.getProjection();
                if (!proj)
                    return;
                const pt = proj.fromLatLngToDivPixel(position);
                if (pt) {
                    div.style.left = `${pt.x}px`;
                    div.style.top = `${pt.y}px`;
                }
            };
            overlay.onRemove = () => {
                if (div.parentNode)
                    div.parentNode.removeChild(div);
            };
            overlay.setMap(map);
            this.pondLabelOverlays.push(overlay);
        });
    }
    clearPondLabels() {
        this.pondLabelOverlays.forEach((o) => o.setMap(null));
        this.pondLabelOverlays = [];
    }
    /**
     * Enables polygon drawing mode.
     * When the user closes a polygon, `onComplete` is called with:
     *   coords    — [lng, lat][] outer ring (GeoJSON order)
     *   areaHa    — area in hectares (4 dp)
     *   centreStr — bounding-box centre as "lat,lng" string (for reverseGeocode)
     */
    setDrawingControl(onComplete) {
        this.editingFeatureId = null;
        this.clearDrawingMode();
        // Remove existing data listeners while drawing
        this.dataListeners.forEach((l) => l.remove());
        this.dataListeners = [];
        // Prevent existing pond polygons from intercepting map clicks during draw mode
        this.mapData.setStyle({ clickable: false });
        this.map.setOptions({ draggableCursor: 'crosshair', disableDoubleClickZoom: true });
        const POLY_STYLE = {
            fillColor: colorTokens.secondary,
            fillOpacity: 0.35,
            strokeColor: colorTokens.secondary,
            strokeWeight: 2,
        };
        // White dot on the first vertex that appears when 3+ points exist —
        // signals to the user "click here to close the polygon" (matches DrawingManager UX)
        let firstVertexDot = null;
        // Compute a screen-size-consistent radius in metres so the dot stays ~9px
        // regardless of zoom level (typical farm drawing zoom: 16–20).
        const dotRadius = () => {
            const zoom = this.map.getZoom() ?? 16;
            const lat = this.drawVertices[0]?.lat() ?? 16;
            return ((156543.03 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom)) * 9;
        };
        const updatePreview = () => {
            if (!this.drawPreview) {
                this.drawPreview = new google.maps.Polygon({
                    ...POLY_STYLE,
                    clickable: false,
                    zIndex: 10,
                    map: this.map,
                });
            }
            this.drawPreview.setPath(this.drawVertices);
            // Show / update first-vertex dot once the polygon can be closed
            if (this.drawVertices.length >= 3) {
                if (!firstVertexDot) {
                    firstVertexDot = new google.maps.Circle({
                        center: this.drawVertices[0],
                        radius: dotRadius(),
                        fillColor: colorTokens.white,
                        fillOpacity: 1,
                        strokeColor: colorTokens.secondary,
                        strokeWeight: 3,
                        clickable: false,
                        zIndex: 12,
                        map: this.map,
                    });
                }
                else {
                    firstVertexDot.setCenter(this.drawVertices[0]);
                    firstVertexDot.setRadius(dotRadius());
                }
            }
        };
        // Returns true when `latlng` is within ~16px of the first drawn vertex
        const isNearFirstVertex = (latlng) => {
            if (this.drawVertices.length < 3)
                return false;
            const proj = this.map.getProjection();
            if (!proj)
                return false;
            const p1 = proj.fromLatLngToPoint(this.drawVertices[0]);
            const p2 = proj.fromLatLngToPoint(latlng);
            if (!p1 || !p2)
                return false;
            const scale = Math.pow(2, this.map.getZoom() ?? 16);
            const dx = (p1.x - p2.x) * scale;
            const dy = (p1.y - p2.y) * scale;
            return Math.sqrt(dx * dx + dy * dy) < 16;
        };
        const finishDrawing = () => {
            const vertices = [...this.drawVertices];
            firstVertexDot?.setMap(null);
            firstVertexDot = null;
            this.clearDrawingMode();
            if (vertices.length < 3)
                return;
            const polygon = new google.maps.Polygon({
                paths: vertices,
                draggable: false,
                editable: true,
                ...POLY_STYLE,
                map: this.map,
            });
            this.currentPolygon = polygon;
            const getPayload = () => {
                const coords = polygon
                    .getPath()
                    .getArray()
                    .map((ll) => [ll.lng(), ll.lat()]);
                const areaM2 = google.maps.geometry.spherical.computeArea(polygon.getPath());
                const areaHa = parseFloat((areaM2 * 0.0001).toFixed(4));
                const bounds = new google.maps.LatLngBounds();
                polygon.getPath().forEach((ll) => bounds.extend(ll));
                const centre = bounds.getCenter();
                return { coords, areaHa, centreStr: `${centre.lat()},${centre.lng()}`, centre };
            };
            const initial = getPayload();
            this.map.panTo(initial.centre);
            onComplete(initial.coords, initial.areaHa, initial.centreStr);
            // Keep polygon editable and propagate geometry changes
            this.currentPolygonPathListeners.forEach((l) => l.remove());
            this.currentPolygonPathListeners = ['insert_at', 'remove_at', 'set_at'].map((eventName) => polygon.getPath().addListener(eventName, () => {
                const p = getPayload();
                // Empty centreStr signals shape-edit so callers skip reverse geocode
                onComplete(p.coords, p.areaHa, '');
            }));
        };
        const clickL = this.map.addListener('click', (e) => {
            if (!e.latLng)
                return;
            // Close polygon when clicking near the first vertex — exactly like DrawingManager
            if (isNearFirstVertex(e.latLng)) {
                finishDrawing();
                return;
            }
            this.drawVertices.push(e.latLng);
            updatePreview();
            if (!this.drawRubberBand) {
                this.drawRubberBand = new google.maps.Polyline({
                    strokeColor: colorTokens.secondary,
                    strokeWeight: 2,
                    strokeOpacity: 0.55,
                    clickable: false,
                    zIndex: 11,
                    map: this.map,
                });
            }
        });
        const moveL = this.map.addListener('mousemove', (e) => {
            if (!e.latLng || this.drawVertices.length === 0 || !this.drawRubberBand)
                return;
            const last = this.drawVertices[this.drawVertices.length - 1];
            this.drawRubberBand.setPath([last, e.latLng]);
            // Cursor hint: pointer when hovering over the first-vertex close target
            this.map.setOptions({
                draggableCursor: isNearFirstVertex(e.latLng) ? 'pointer' : 'crosshair',
            });
        });
        // Double-click also closes the polygon.
        // Do NOT pop here — in Maps API v3.65 the number of preceding click events
        // before dblclick is inconsistent; finishDrawing copies vertices before clearing.
        const dblClickL = this.map.addListener('dblclick', () => {
            if (this.drawVertices.length >= 3)
                finishDrawing();
        });
        this.drawListeners.push(clickL, moveL, dblClickL);
    }
    /**
     * Starts editing an existing pond boundary from the map data layer.
     * Returns false when feature geometry cannot be resolved.
     */
    startEditBoundary(featureId, onChange) {
        this.clearDrawnPolygon();
        const feature = this.mapData.getFeatureById(featureId);
        if (!feature)
            return false;
        const geometry = feature.getGeometry();
        if (!geometry)
            return false;
        const polygonGeometry = geometry;
        const ring = polygonGeometry.getArray()?.[0]?.getArray?.();
        if (!Array.isArray(ring) || ring.length < 3)
            return false;
        const polygon = new google.maps.Polygon({
            paths: ring,
            editable: true,
            draggable: false,
            fillColor: colorTokens.secondary,
            fillOpacity: 0.35,
            strokeColor: colorTokens.secondary,
            strokeWeight: 2,
            map: this.map,
        });
        this.mapData.overrideStyle(feature, { visible: false });
        this.currentPolygon = polygon;
        this.editingFeatureId = featureId;
        const emit = () => {
            const coords = polygon
                .getPath()
                .getArray()
                .map((ll) => [ll.lng(), ll.lat()]);
            const areaM2 = google.maps.geometry.spherical.computeArea(polygon.getPath());
            const areaHa = parseFloat((areaM2 * 0.0001).toFixed(4));
            onChange(coords, areaHa);
        };
        this.currentPolygonPathListeners.forEach((l) => l.remove());
        this.currentPolygonPathListeners = ['insert_at', 'remove_at', 'set_at'].map((eventName) => polygon.getPath().addListener(eventName, emit));
        emit();
        const center = feature.getProperty('center') ?? null;
        if (center)
            this.map.panTo(center);
        return true;
    }
    /** Removes the polygon drawn by the user (if any) and cancels any in-progress draw. */
    clearDrawnPolygon() {
        this.clearDrawingMode();
        if (this.currentPolygon) {
            this.currentPolygon.setMap(null);
            this.currentPolygon = null;
        }
        if (this.editingFeatureId) {
            const editedFeature = this.mapData.getFeatureById(this.editingFeatureId);
            if (editedFeature)
                this.mapData.revertStyle(editedFeature);
            this.editingFeatureId = null;
        }
        this.currentPolygonPathListeners.forEach((l) => l.remove());
        this.currentPolygonPathListeners = [];
        // Restore the DO-based style function that was replaced with { clickable: false }
        // during setDrawingControl(). Without this, all pond polygons render as black outlines.
        this.setOptions('DATA');
    }
    /**
     * Reverse geocodes a "lat,lng" string into structured place information.
     * Resolves with an empty object on failure (non-fatal).
     */
    reverseGeocode(latLngStr) {
        const parts = latLngStr.split(',');
        const lat = parseFloat(parts[0]);
        const lng = parseFloat(parts[1]);
        if (Number.isNaN(lat) || Number.isNaN(lng)) {
            return Promise.resolve({});
        }
        return new Promise((resolve) => {
            this.geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                if (status !== google.maps.GeocoderStatus.OK || !results?.length) {
                    resolve({});
                    return;
                }
                const components = results[0].address_components;
                const getComp = (type) => components.find((c) => c.types.includes(type))?.long_name;
                resolve({
                    state: getComp('administrative_area_level_1'),
                    district: getComp('administrative_area_level_3') ?? getComp('administrative_area_level_2'),
                    pincode: getComp('postal_code'),
                    formattedAddress: results[0].formatted_address,
                    city: getComp('locality') ?? getComp('administrative_area_level_2'),
                    country: getComp('country'),
                });
            });
        });
    }
    /**
     * Attaches click, hover, and mouse-out listeners to the data layer.
     * Replaces any previously attached data listeners.
     */
    addMapDataListeners(listeners) {
        this.dataListeners.forEach((l) => l.remove());
        this.dataListeners = [];
        this.dataListeners.push(this.mapData.addListener('click', ({ feature }) => {
            listeners.onClick(feature);
        }), this.mapData.addListener('mouseover', (event) => {
            const dom = event.domEvent;
            listeners.onHover(event.feature, dom.clientX, dom.clientY);
        }), this.mapData.addListener('mouseout', () => {
            listeners.onMouseOut();
        }));
    }
    /** Removes all map and data event listeners and clears pond labels. */
    removeAllListeners() {
        this.eventListeners.forEach((l) => l.remove());
        this.dataListeners.forEach((l) => l.remove());
        this.eventListeners = [];
        this.dataListeners = [];
        this.currentPolygonPathListeners.forEach((l) => l.remove());
        this.currentPolygonPathListeners = [];
        this.clearPondLabels();
    }
}
